<script setup lang="ts">
import L, { type LayerGroup, type Map as LeafletMap, type Marker, type TileLayer } from 'leaflet'
import { onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue'

import type { BasemapKind, MapMode, MapViewport } from '@/components/map/mapMode'
import { useMapViewportRestore } from '@/composables/useMapViewportRestore'
import { useTheme } from '@/composables/useTheme'
import type { BusRoute, DirectionKey, MapUserLocation, MapVehicle, Stop } from '@/types'
import { decodePolyline } from '@/utils/polyline'

const props = withDefaults(
  defineProps<{
    mode: MapMode
    userLocation?: MapUserLocation
    basemap?: BasemapKind
    /** Opt in to emitting `pickPoint` on a map tap (the planner's drop-a-pin). */
    pickPoint?: boolean
    /**
     * Distinguishes maps within one screen for the per-history-entry camera
     * memory. Only needed if a screen ever mounts two; none does today.
     */
    restoreId?: string
  }>(),
  { basemap: 'map' },
)

const emit = defineEmits<{
  selectStop: [stopId: number]
  /** The popover's action, distinct from merely selecting a marker. */
  viewStopDetails: [stopId: number]
  /** Raw map tap. Only wired when `pickPoint` is set, so it cannot fire by accident. */
  pickPoint: [point: { lat: number; lng: number }]
  /**
   * Fired on `moveend`, not `move`. The home screen recomputes which stops are
   * visible from this, and doing that on every animation frame of a pan would
   * spend the whole frame budget on index queries.
   */
  viewportChange: [viewport: MapViewport]
}>()

const { resolved: resolvedTheme } = useTheme()

/**
 * Where this map was when the user last left this history entry. Back should land
 * on it; a fresh push should not see it at all. The store and its keying rules
 * live in `utils/historyMemory.ts`.
 */
const viewportMemory = useMapViewportRestore(props.restoreId)

const mapElement = useTemplateRef<HTMLElement>('map')
let leafletMap: LeafletMap | undefined
let routeLayers: LayerGroup | undefined
let stopLayers: LayerGroup | undefined
let vehicleLayers: LayerGroup | undefined
let userLocationLayers: LayerGroup | undefined
let resizeObserver: ResizeObserver | undefined
let basemapLayer: TileLayer | undefined
let vehicleMarkers = new Map<string, Marker>()
let stopMarkers = new Map<number, Marker>()

/**
 * Basemap sources.
 *
 * CARTO ships the same tile geometry in a light and a dark cut, so the theme
 * swap within `map` is a URL change rather than a different provider —
 * attribution and zoom limits stay valid across both.
 *
 * Satellite is Esri World Imagery: no API key, no account, and usable with
 * attribution. It has no dark cut, and aerial photography does not need one, so
 * the theme swap is skipped entirely while it is selected. Its `subdomains` and
 * `maxZoom` differ from CARTO's, which is why switching source rebuilds the
 * layer rather than calling setUrl.
 */
const BASEMAP_SOURCES = {
  map: {
    url: (theme: 'light' | 'dark') =>
      `https://{s}.basemaps.cartocdn.com/${theme === 'dark' ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png`,
    options: {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
    },
  },
  satellite: {
    url: () =>
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    options: {
      attribution:
        'Tiles &copy; <a href="https://www.esri.com">Esri</a> — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      maxZoom: 19,
    },
  },
} as const

function createBasemap(kind: BasemapKind, theme: 'light' | 'dark') {
  const source = BASEMAP_SOURCES[kind]
  return L.tileLayer(source.url(theme), { ...source.options })
}

/**
 * The only hardcoded colors in the component layer, and deliberately so.
 *
 * They are used solely when the stylesheet has not resolved — notably under
 * unit tests, where there is no CSS and getPropertyValue() returns ''. Without
 * them Leaflet receives an empty color string. They mirror the light-theme
 * values in styles.css and are NOT a second source of truth for the running
 * app: in the browser the custom property always wins.
 */
const THEME_FALLBACK = {
  '--foreground': '#262626',
  '--muted-foreground': '#6b6b6b',
  '--background': '#ffffff',
  '--ring': '#1c69d4',
  '--route-outbound': '#15803d',
  '--route-inbound': '#ea580c',
} as const

/**
 * Leaflet styles vectors through its JS API, which takes color strings rather
 * than classes, so these cannot inherit the theme the way the rest of the UI
 * does. Reading the token off the document root keeps DESIGN.md's rule that no
 * component names a color, and lets the vectors follow light/dark.
 */
function themeColor(token: keyof typeof THEME_FALLBACK): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim()
  return value || THEME_FALLBACK[token]
}

/**
 * Direction encoding. The dash pattern is not decorative — it carries the same
 * information as the colour, so the two directions stay distinguishable without
 * relying on hue alone.
 */
function directionStyles(): Record<DirectionKey, { color: string; dashArray?: string }> {
  return {
    outbound: { color: themeColor('--route-outbound') },
    inbound: { color: themeColor('--route-inbound'), dashArray: '7 6' },
  }
}

onMounted(() => {
  if (!mapElement.value) return

  /**
   * Read before construction, and handed to the constructor below rather than
   * applied with a `setView` afterwards. That ordering is the whole difference
   * between "back returns to where I was" and "back flashes the default view,
   * requests its tiles, then jumps" — Leaflet never lays out the default at all.
   */
  const restoredView = viewportMemory.remembered()

  leafletMap = L.map(mapElement.value, {
    /**
     * Removes only Leaflet's own "Leaflet" branding link, which is a courtesy
     * credit rather than a licence term.
     *
     * The OpenStreetMap and CARTO credits below STAY. OSM data is ODbL, which
     * requires visible attribution, and CARTO's basemap terms require theirs —
     * removing those would be a licence breach, not a styling choice.
     */
    attributionControl: true,
    // The defaults govern a fresh push only; a remembered entry overrides both.
    // Street level so the stop layer is visible immediately: at the old default
    // of 12 the home map sat below STOP_ZOOM_FLOOR and greeted the user with an
    // empty map and a "zoom in" hint. `route` and `vehicle` modes call fitBounds
    // on mount, so this only governs `stops` mode.
    center: restoredView ? [restoredView.lat, restoredView.lng] : [21.0285, 105.8542],
    zoom: restoredView?.zoom ?? 15,
    zoomControl: false,
    preferCanvas: true,
  })

  // Assigned before addTo: Leaflet returns `this` from addTo, but keeping the
  // reference explicit means the theme watcher cannot silently hold undefined.
  basemapLayer = createBasemap(props.basemap, resolvedTheme.value)
  basemapLayer.addTo(leafletMap)
  leafletMap.attributionControl.setPrefix('')

  /**
   * No zoom control. It sat in the same corner as the app's own map controls
   * and the mockups have none — pinch, double-tap and scroll-wheel all still
   * zoom. `zoomControl: false` above stops Leaflet adding its default one.
   */
  routeLayers = L.layerGroup().addTo(leafletMap)
  stopLayers = L.layerGroup().addTo(leafletMap)
  vehicleLayers = L.layerGroup().addTo(leafletMap)
  userLocationLayers = L.layerGroup().addTo(leafletMap)

  leafletMap.on('moveend', onMoveEnd)

  /**
   * The reused-instance path. `RouterView` keeps one component across a
   * params-only change — `/routes/09` → `/routes/32`, `/stops/2/buses` →
   * `/stops/1/buses` — so back between them never remounts this component and the
   * constructor above never runs again.
   */
  viewportMemory.onNavigate((view) => {
    // `animate: false`: an animated restore reads as the app moving the map AFTER
    // arrival, which is the very thing that makes losing the position feel wrong.
    leafletMap?.setView([view.lat, view.lng], view.zoom, { animate: false })
  })

  if (props.pickPoint) {
    leafletMap.on('click', (event) => {
      emit('pickPoint', { lat: event.latlng.lat, lng: event.latlng.lng })
    })
  }

  // A remembered camera outranks fit-to-route: the user zoomed in on one bus,
  // opened it, and came back — reframing the whole route would discard exactly
  // what they came back to. A fresh push still fits.
  redrawRoute(!restoredView)
  redrawStops()
  redrawVehicles()
  redrawUserLocation(false)
  // Reports the restored bounds, not the default's, so `useVisibleStops` queries
  // the stops the user is actually looking at.
  emitViewport()

  resizeObserver = new ResizeObserver(() => leafletMap?.invalidateSize())
  resizeObserver.observe(mapElement.value)
  window.setTimeout(() => leafletMap?.invalidateSize(), 50)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  leafletMap?.remove()
  leafletMap = undefined
  basemapLayer = undefined
})

/**
 * Leaflet caches vector styles at construction, so a theme change would
 * otherwise leave light-mode ink on a dark canvas. Marker divIcons are built
 * from Tailwind classes and follow the theme on their own; only the polylines
 * and the user-location circle need rebuilding.
 *
 * Watching the shared mode is preferred over a MutationObserver on the `dark`
 * class: the app already owns the state that drives that class.
 *
 * `flush: 'post'` is load-bearing, not incidental. themeColor() reads the token
 * off the document root, and useColorMode writes the `dark` class from its own
 * post-flush watcher. A default (pre-flush) watcher here would run first and
 * resolve the colors the theme is being switched *away* from, leaving the map
 * one toggle behind.
 */
watch(
  resolvedTheme,
  (theme) => {
    // setUrl keeps the layer, so viewport, zoom and every overlay survive the
    // swap — recreating the tile layer would drop the map's stacking order.
    // Skipped under satellite: aerial photography has no dark cut, and pointing
    // it at a CARTO URL would silently swap the provider.
    if (props.basemap === 'map') basemapLayer?.setUrl(BASEMAP_SOURCES.map.url(theme))
    redrawRoute(false)
    redrawUserLocation(false)
  },
  { flush: 'post' },
)

/**
 * Switching mode kind swaps the entire layer set, so it refits the viewport.
 * Changing route within the same kind does too; changing only the vehicles must
 * not, or the map would yank itself back on every live-data refresh.
 *
 * The refit is suppressed when the entry being arrived at has a remembered
 * camera. `onNavigate` above would re-apply it a tick later anyway, but this
 * watcher runs on the pre-flush queue, so without the guard the refit paints for
 * one frame first — a visible yank on every back between two routes.
 */
watch(
  () => [props.mode.kind, routeOf(props.mode)?.id] as const,
  () => {
    redrawRoute(!viewportMemory.remembered())
    redrawStops()
    redrawVehicles()
  },
)

watch(
  () => props.basemap,
  (kind) => {
    if (!leafletMap || !basemapLayer) return
    leafletMap.removeLayer(basemapLayer)
    basemapLayer = createBasemap(kind, resolvedTheme.value)
    basemapLayer.addTo(leafletMap)
    // A freshly added layer lands on top of the overlay panes, so it has to be
    // pushed back under the routes, stops and vehicles it is a backdrop for.
    basemapLayer.bringToBack()
  },
)

watch(
  () => (props.mode.kind === 'stops' ? props.mode.stops : undefined),
  () => redrawStops(),
)

watch(
  () => (props.mode.kind === 'stops' ? props.mode.showSelectedStopPopup : undefined),
  () => redrawStops(),
)

watch(
  () => (props.mode.kind === 'stops' ? props.mode.selectedStopId : undefined),
  (stopId) => {
    redrawStops({ autoPanSelected: true })
    if (stopId === undefined || props.mode.kind !== 'stops') return
    const stop = props.mode.stops.find((candidate) => candidate.id === stopId)
    if (!stop || !leafletMap) return
    /**
     * A stop the user can already see was almost certainly tapped on the map,
     * and recentring it would move the ground under the finger that just tapped.
     * Leaflet's popup `autoPan` still nudges the view if the popover would be
     * clipped at an edge — the minimum move, rather than a recentre.
     */
    if (leafletMap.getBounds().contains([stop.lat, stop.lng])) return
    // Selecting a stop from a list is a request to see WHERE it is, so an
    // off-screen one is brought into view rather than only recoloured.
    leafletMap.setView([stop.lat, stop.lng], Math.max(leafletMap.getZoom(), 16), {
      animate: true,
      duration: 0.5,
    })
  },
)

watch(
  () => (props.mode.kind === 'vehicle' ? props.mode.targetStop : undefined),
  () => redrawStops(),
)

watch(
  () => (props.mode.kind === 'route' ? props.mode.vehicles : undefined),
  () => redrawVehicles(),
)

watch(
  () => (props.mode.kind === 'vehicle' ? props.mode.vehicle : undefined),
  () => redrawVehicles(),
)

watch(
  () => (props.mode.kind === 'route' ? props.mode.selectedVehicleId : undefined),
  (vehicleId) => {
    redrawVehicles()
    if (!vehicleId) return
    const marker = vehicleMarkers.get(vehicleId)
    if (!marker) return
    leafletMap?.panTo(marker.getLatLng(), { animate: true, duration: 0.45 })
    marker.openTooltip()
  },
)

watch(
  () => props.userLocation,
  () => redrawUserLocation(true),
)

/**
 * Bound to `moveend`, never to `move`. Both halves are the reason: `move` fires on
 * every animation frame of a pan, and neither an index query nor a store write
 * belongs in that budget.
 *
 * Programmatic moves — fit-to-route, the location fly-to, select-a-stop — land
 * here too, and that is correct: whatever the map settled on IS where the user
 * left this entry.
 */
function onMoveEnd() {
  if (!leafletMap) return
  emitViewport()
  const center = leafletMap.getCenter()
  viewportMemory.save({ lat: center.lat, lng: center.lng, zoom: leafletMap.getZoom() })
}

function emitViewport() {
  if (!leafletMap) return
  const bounds = leafletMap.getBounds()
  emit('viewportChange', {
    zoom: leafletMap.getZoom(),
    bounds: {
      south: bounds.getSouth(),
      west: bounds.getWest(),
      north: bounds.getNorth(),
      east: bounds.getEast(),
    },
  })
}

/** The route being drawn, if the current mode has one. */
function routeOf(mode: MapMode) {
  return mode.kind === 'stops' ? undefined : mode.route
}

/** Vehicles to draw: all of them in route mode, exactly one in vehicle mode. */
function vehiclesOf(mode: MapMode): MapVehicle[] {
  if (mode.kind === 'route') return mode.vehicles
  if (mode.kind === 'vehicle') return mode.vehicle ? [mode.vehicle] : []
  return []
}

/**
 * Both directions, always. The mockups carry no direction filter — a route
 * screen shows the whole route — so the previous `direction` prop and its
 * per-direction filtering are gone.
 */
const DIRECTIONS: readonly DirectionKey[] = ['outbound', 'inbound']

function redrawRoute(fitBounds: boolean) {
  if (!leafletMap || !routeLayers) return
  routeLayers.clearLayers()

  const route = routeOf(props.mode)
  if (!route) return

  const allPoints: L.LatLngExpression[] = []

  for (const direction of DIRECTIONS) {
    const routeDirection = route.directions[direction]
    let points = decodePolyline(routeDirection.polyline)
    if (points.length < 2) {
      points = routeDirection.stops.map((stop) => [stop.lat, stop.lng])
    }
    if (points.length === 0) continue

    allPoints.push(...points)
    const style = directionStyles()[direction]

    // Casing sits under the coloured line to lift it off the basemap's own
    // road network. Kept proportional at roughly 2x the line weight.
    L.polyline(points, {
      color: themeColor('--background'),
      weight: 7,
      opacity: 0.94,
      lineCap: 'round',
      lineJoin: 'round',
    }).addTo(routeLayers)

    // 4px, down from the original 5px. 3px was tried and read too faint at city
    // zoom — a mid-tone line on a pale basemap needs the extra pixel.
    L.polyline(points, {
      color: style.color,
      dashArray: style.dashArray,
      weight: 4,
      opacity: 0.96,
      lineCap: 'round',
      lineJoin: 'round',
    })
      .bindTooltip(direction === 'outbound' ? 'Outbound route' : 'Inbound route', {
        sticky: true,
      })
      .addTo(routeLayers)

    // Stops along a route are context, not targets — the interactive stop layer
    // belongs to `stops` mode. Canvas dots, so a 94-stop route costs no DOM.
    for (const stop of routeDirection.stops) {
      L.circleMarker([stop.lat, stop.lng], {
        radius: 3,
        color: themeColor('--background'),
        weight: 2,
        fillColor: style.color,
        fillOpacity: 1,
        interactive: false,
      }).addTo(routeLayers)
    }
  }

  const [startStop, endStop] = routeEndpointStops(route)
  if (startStop) {
    L.marker([startStop.lat, startStop.lng], {
      icon: createRouteEndpointIcon('A', '--route-outbound'),
      interactive: false,
    }).addTo(routeLayers)
  }
  if (endStop && endStop.id !== startStop?.id) {
    L.marker([endStop.lat, endStop.lng], {
      icon: createRouteEndpointIcon('B', '--route-inbound'),
      interactive: false,
    }).addTo(routeLayers)
  }

  if (fitBounds && allPoints.length > 1) {
    leafletMap.fitBounds(L.latLngBounds(allPoints), {
      paddingTopLeft: [48, 130],
      paddingBottomRight: [48, 170],
      maxZoom: 14,
      animate: false,
    })
  }
}

/**
 * The outbound direction names the route's physical terminals in A → B order.
 * If a partial dataset has no outbound stops, reverse the inbound list so the
 * endpoint labels still follow the departure → return convention.
 */
function routeEndpointStops(route: BusRoute): [Stop | undefined, Stop | undefined] {
  const outboundStops = route.directions.outbound.stops
  const stops =
    outboundStops.length >= 2 ? outboundStops : [...route.directions.inbound.stops].reverse()
  return [stops[0], stops.at(-1)]
}

interface RedrawStopsOptions {
  autoPanSelected?: boolean
}

/**
 * `stops` mode. Compact divIcons keep the stop affordance legible at street
 * zoom: a square bus-stop glyph is easier to distinguish than a dense field of
 * canvas dots. The visible-stop cap keeps the DOM bounded on the home map.
 */
function redrawStops({ autoPanSelected = false }: RedrawStopsOptions = {}) {
  if (!leafletMap || !stopLayers) return
  stopLayers.clearLayers()
  stopMarkers = new Map()

  const mode = props.mode

  if (mode.kind === 'stops') {
    const { stops, selectedStopId, showSelectedStopPopup = true } = mode

    for (const stop of stops) {
      const selected = stop.id === selectedStopId
      const marker = L.marker([stop.lat, stop.lng], {
        icon: L.divIcon({
          className: 'bg-transparent',
          html: createStopMarker(stop, selected),
          // Keep a 44px hit target while painting only a 22×28px pin, so dense
          // stops stay tappable without covering the road underneath.
          iconSize: [44, 44],
          iconAnchor: [22, 44],
          popupAnchor: [0, -34],
        }),
        bubblingMouseEvents: false,
      })
        // No hover tooltip. It repeated the popover's own title, and on a
        // touchscreen it fired on the same tap that opened the popover — two
        // labels for one stop, one of which no touch user can dismiss.
        .on('click', () => emit('selectStop', stop.id))
        .addTo(stopLayers)

      /**
       * Bound to the selected stop only, never to all of them. Stop icons are bounded
       * by the visible-stop cap, while popup content remains limited to the one
       * selected marker. A popup per marker would still overwhelm the map and would
       * be destroyed and recreated on every viewport redraw.
       *
       * So a tap goes: click → `selectStop` → the screen stores the id → this
       * redraw binds and opens the popover on the new marker.
       */
      if (selected && showSelectedStopPopup) {
        marker.bindPopup(createStopPopup(stop), {
          autoPan: autoPanSelected,
          offset: [0, -6],
          closeButton: false,
        })
        marker.openPopup()
      }

      stopMarkers.set(stop.id, marker)
    }
  } else if (mode.kind === 'vehicle' && mode.targetStop) {
    const stop = mode.targetStop
    const marker = L.marker([stop.lat, stop.lng], {
      icon: L.divIcon({
        className: 'bg-transparent',
        html: createStopMarker(stop, true),
        iconSize: [44, 44],
        iconAnchor: [22, 44],
        popupAnchor: [0, -34],
      }),
      bubblingMouseEvents: false,
    }).addTo(stopLayers)

    stopMarkers.set(stop.id, marker)
  }
}

function redrawVehicles() {
  if (!leafletMap || !vehicleLayers) return
  vehicleLayers.clearLayers()
  vehicleMarkers = new Map()

  const mode = props.mode
  const route = routeOf(mode)
  // In `vehicle` mode the single bus IS the subject, so it reads as selected
  // without anything having to select it — which is also what pins its ETA
  // tooltip open, matching the mockup's permanent "7 mins" label.
  const selectedId =
    mode.kind === 'route'
      ? mode.selectedVehicleId
      : mode.kind === 'vehicle'
        ? mode.vehicle?.id
        : undefined

  for (const vehicle of vehiclesOf(mode)) {
    const selected = vehicle.id === selectedId
    const icon = L.divIcon({
      className: '',
      html: createVehicleMarker(vehicle, vehicleBearing(vehicle, route)),
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      tooltipAnchor: [0, -20],
    })

    const marker = L.marker([vehicle.lat, vehicle.lng], {
      icon,
      zIndexOffset: selected ? 1_000 : 500,
      riseOnHover: true,
    })
      .bindTooltip(createVehicleTooltip(vehicle), {
        direction: 'top',
        interactive: true,
        offset: [0, -4],
        // Leaflet tooltips are hover-scoped by default, so the selected
        // vehicle's tooltip closed again on the next mouseout even though
        // openTooltip() had been called. Selection has to pin it.
        permanent: selected,
      })
      .addTo(vehicleLayers)

    vehicleMarkers.set(vehicle.id, marker)
  }
}

function redrawUserLocation(focus: boolean) {
  if (!leafletMap || !userLocationLayers) return
  userLocationLayers.clearLayers()
  if (!props.userLocation) return

  const point = L.latLng(props.userLocation.lat, props.userLocation.lng)

  L.circle(point, {
    radius: Math.max(props.userLocation.accuracy, 10),
    color: themeColor('--foreground'),
    opacity: 0.35,
    weight: 1,
    fillColor: themeColor('--muted-foreground'),
    fillOpacity: 0.08,
    interactive: false,
  }).addTo(userLocationLayers)

  L.marker(point, {
    icon: createUserLocationIcon(),
    keyboard: true,
    title: 'Your location',
    alt: 'Your location',
    zIndexOffset: 2_000,
  })
    .bindTooltip(`Your location · within ${Math.round(props.userLocation.accuracy)} m`, {
      direction: 'top',
      offset: [0, -14],
    })
    .addTo(userLocationLayers)

  if (focus) {
    leafletMap.flyTo(point, Math.max(leafletMap.getZoom(), 16), {
      animate: true,
      duration: 0.75,
    })
  }
}

function createUserLocationIcon() {
  const target = document.createElement('div')
  target.className = 'flex size-10 items-center justify-center'

  const marker = document.createElement('span')
  marker.className =
    'size-4 rounded-full bg-primary ring-4 ring-background outline outline-1 outline-foreground/20'
  target.append(marker)

  return L.divIcon({
    className: 'bg-transparent',
    html: target,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    tooltipAnchor: [0, -14],
  })
}

function createRouteEndpointIcon(
  label: 'A' | 'B',
  colorToken: '--route-outbound' | '--route-inbound',
) {
  const namespace = 'http://www.w3.org/2000/svg'
  const marker = document.createElement('div')
  const icon = document.createElementNS(namespace, 'svg')
  const pin = document.createElementNS(namespace, 'path')
  const center = document.createElementNS(namespace, 'circle')
  const text = document.createElementNS(namespace, 'text')
  const color = themeColor(colorToken)

  marker.className = 'flex h-[50px] w-[42px] items-end justify-center'
  marker.dataset.mapRouteEndpoint = label
  marker.setAttribute('aria-label', `Route ${label} endpoint`)

  icon.setAttribute('viewBox', '0 0 42 50')
  icon.setAttribute('aria-hidden', 'true')
  icon.classList.add('h-[50px]', 'w-[42px]', 'overflow-visible', 'drop-shadow-sm')

  pin.dataset.mapRouteEndpointShape = ''
  pin.setAttribute('d', 'M21 49C19.2 45.4 4 31.8 4 20A17 17 0 1 1 38 20C38 31.8 22.8 45.4 21 49Z')
  pin.setAttribute('fill', color)
  pin.setAttribute('stroke', 'var(--background)')
  pin.setAttribute('stroke-width', '1.5')

  center.setAttribute('cx', '21')
  center.setAttribute('cy', '20')
  center.setAttribute('r', '11.5')
  center.setAttribute('fill', 'var(--background)')

  text.dataset.mapRouteEndpointLabel = ''
  text.setAttribute('x', '21')
  text.setAttribute('y', '20')
  text.setAttribute('text-anchor', 'middle')
  text.setAttribute('dominant-baseline', 'central')
  text.setAttribute('font-family', 'ui-sans-serif, sans-serif')
  text.setAttribute('font-size', '16')
  text.setAttribute('font-weight', '700')
  text.setAttribute('fill', color)
  text.textContent = label

  icon.append(pin, center, text)
  marker.append(icon)

  return L.divIcon({
    className: 'bg-transparent',
    html: marker,
    iconSize: [42, 50],
    iconAnchor: [21, 50],
  })
}

function createStopMarker(stop: Stop, selected: boolean) {
  const namespace = 'http://www.w3.org/2000/svg'
  const marker = document.createElement('div')
  const icon = document.createElementNS(namespace, 'svg')
  const shape = document.createElementNS(namespace, 'path')
  const selection = selected ? document.createElementNS(namespace, 'path') : undefined
  const pole = document.createElementNS(namespace, 'path')
  const stopSign = document.createElementNS(namespace, 'circle')
  const busBody = document.createElementNS(namespace, 'rect')
  const busWindows = document.createElementNS(namespace, 'path')
  const leftLight = document.createElementNS(namespace, 'circle')
  const rightLight = document.createElementNS(namespace, 'circle')
  const leftWheel = document.createElementNS(namespace, 'circle')
  const rightWheel = document.createElementNS(namespace, 'circle')
  const pinPath =
    'M6 1.75H28C30.35 1.75 32.25 3.65 32.25 6V26C32.25 28.35 30.35 30.25 28 30.25H20.5L17 40.25L13.5 30.25H6C3.65 30.25 1.75 28.35 1.75 26V6C1.75 3.65 3.65 1.75 6 1.75Z'

  marker.className = 'flex size-11 items-end justify-center'
  marker.setAttribute('aria-label', `${stop.name} bus stop`)

  icon.dataset.mapStopPin = ''
  icon.setAttribute('viewBox', '0 0 34 42')
  icon.setAttribute('aria-hidden', 'true')
  icon.classList.add('h-7', 'w-[22px]', 'overflow-visible', 'drop-shadow-sm')

  if (selection) {
    selection.dataset.mapStopSelection = ''
    selection.setAttribute('d', pinPath)
    selection.setAttribute('fill', 'none')
    selection.setAttribute('stroke', 'var(--ring)')
    selection.setAttribute('stroke-width', '4')
    selection.setAttribute('stroke-linejoin', 'round')
    selection.setAttribute('opacity', '0.55')
    icon.append(selection)
  }

  shape.dataset.mapStopShape = ''
  shape.setAttribute('d', pinPath)
  shape.setAttribute('fill', 'var(--background)')
  shape.setAttribute('stroke', 'var(--primary)')
  shape.setAttribute('stroke-width', '1.75')
  shape.setAttribute('stroke-linejoin', 'round')

  const lineElements = [pole, busBody, busWindows]
  for (const element of lineElements) {
    element.setAttribute('fill', 'none')
    element.setAttribute('stroke', 'var(--primary)')
    element.setAttribute('stroke-width', '1.75')
    element.setAttribute('stroke-linecap', 'round')
    element.setAttribute('stroke-linejoin', 'round')
  }

  pole.setAttribute('d', 'M8 11.5V25')
  stopSign.setAttribute('cx', '8')
  stopSign.setAttribute('cy', '9')
  stopSign.setAttribute('r', '2.25')
  busBody.setAttribute('x', '13')
  busBody.setAttribute('y', '7.5')
  busBody.setAttribute('width', '14')
  busBody.setAttribute('height', '15.5')
  busBody.setAttribute('rx', '2')
  busWindows.setAttribute('d', 'M13 14.5h14M20 7.5v7')

  const filledElements = [stopSign, leftLight, rightLight, leftWheel, rightWheel]
  for (const element of filledElements) element.setAttribute('fill', 'var(--primary)')
  leftLight.setAttribute('cx', '16')
  leftLight.setAttribute('cy', '18.5')
  leftLight.setAttribute('r', '1')
  rightLight.setAttribute('cx', '24')
  rightLight.setAttribute('cy', '18.5')
  rightLight.setAttribute('r', '1')
  leftWheel.setAttribute('cx', '16')
  leftWheel.setAttribute('cy', '24.75')
  leftWheel.setAttribute('r', '1.4')
  rightWheel.setAttribute('cx', '24')
  rightWheel.setAttribute('cy', '24.75')
  rightWheel.setAttribute('r', '1.4')

  icon.append(
    shape,
    pole,
    stopSign,
    busBody,
    busWindows,
    leftLight,
    rightLight,
    leftWheel,
    rightWheel,
  )
  marker.append(icon)
  return marker
}

function createDirectionArrow(bearing: number) {
  const namespace = 'http://www.w3.org/2000/svg'
  const icon = document.createElementNS(namespace, 'svg')
  const arrow = document.createElementNS(namespace, 'path')

  icon.dataset.mapDirectionArrow = ''
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('fill', 'currentColor')
  icon.setAttribute('aria-hidden', 'true')
  icon.classList.add('size-5', 'shrink-0')
  icon.style.transform = `rotate(${Math.round(bearing)}deg)`
  icon.style.transformOrigin = 'center'

  // Navigation pointer aimed north at 0deg; CSS rotation follows map bearing.
  arrow.setAttribute('d', 'M12 1.5 22 20.5 12 16.75 2 20.5 12 1.5Z')
  icon.append(arrow)
  return icon
}

function vehicleBearing(vehicle: MapVehicle, route: BusRoute | undefined): number {
  const fallback = vehicle.direction === 'outbound' ? 90 : 270
  if (!route) return fallback

  const direction = route.directions[vehicle.direction]
  let points = decodePolyline(direction.polyline)
  if (points.length < 2) points = direction.stops.map((stop) => [stop.lat, stop.lng])
  if (points.length < 2) return fallback

  const longitudeScale = Math.cos((vehicle.lat * Math.PI) / 180)
  let nearestDistance = Number.POSITIVE_INFINITY
  let nearestBearing = fallback

  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index]!
    const end = points[index + 1]!
    const segmentX = (end[1] - start[1]) * longitudeScale
    const segmentY = end[0] - start[0]
    const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY
    if (segmentLengthSquared === 0) continue

    const vehicleX = (vehicle.lng - start[1]) * longitudeScale
    const vehicleY = vehicle.lat - start[0]
    const projection = Math.max(
      0,
      Math.min(1, (vehicleX * segmentX + vehicleY * segmentY) / segmentLengthSquared),
    )
    const deltaX = vehicleX - projection * segmentX
    const deltaY = vehicleY - projection * segmentY
    const distanceSquared = deltaX * deltaX + deltaY * deltaY
    if (distanceSquared >= nearestDistance) continue

    nearestDistance = distanceSquared
    nearestBearing = (Math.atan2(segmentX, segmentY) * 180) / Math.PI
  }

  return (nearestBearing + 360) % 360
}

function createVehicleMarker(vehicle: MapVehicle, bearing: number) {
  const marker = document.createElement('div')
  const disc = document.createElement('div')
  // The live payload has no compass heading, so the pointer follows the nearest
  // segment of this vehicle's direction-specific route geometry.
  // The transparent wrapper keeps a 44px target while the visible disc stays
  // close to the stop pin's 22px painted width.
  marker.className = 'flex size-11 items-center justify-center'
  marker.setAttribute(
    'aria-label',
    `${vehicle.routeCode} ${vehicle.label}, ${vehicle.direction} direction`,
  )
  if (vehicle.source === 'last-known') marker.classList.add('opacity-60')

  disc.dataset.mapVehicleDisc = ''
  disc.className =
    'flex size-[26px] items-center justify-center rounded-full bg-primary/85 text-primary-foreground shadow-md ring-2 ring-primary-foreground'
  disc.append(createDirectionArrow(bearing))
  marker.append(disc)

  const label = document.createElement('span')
  label.className = 'sr-only'
  label.textContent = compactVehicleLabel(vehicle)
  marker.append(label)
  return marker
}

/**
 * Popover content for a selected stop, and the only visible label a stop pin carries —
 * every screen in `stops` mode gets it, so the interaction is the same whether
 * the map is the home screen's or the look-up tab's split view.
 *
 * Built as DOM rather than a Vue component because Leaflet popups take HTML or
 * an element, not a component — the same reason createVehicleTooltip exists.
 *
 * The `pointerdown` stopPropagation is load-bearing: without it Leaflet's own
 * map-click handler fires first and closes the popup before the button's click
 * ever lands. That works with a mouse in devtools and fails on a touchscreen.
 */
function createStopPopup(stop: Stop) {
  const content = document.createElement('div')
  content.className = 'w-44'

  const label = document.createElement('div')
  label.className = 'label-uppercase text-xs text-muted-foreground'
  label.textContent = 'Bus stop'

  const title = document.createElement('strong')
  title.className = 'mt-0.5 block text-xs leading-snug'
  title.textContent = stop.name

  const action = document.createElement('button')
  action.type = 'button'
  action.className =
    'mt-2 inline-flex min-h-11 w-full items-center justify-center bg-primary px-2 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary-active'
  action.textContent = 'View details'
  action.addEventListener('pointerdown', (event) => event.stopPropagation())
  action.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    emit('viewStopDetails', stop.id)
  })

  content.append(label, title, action)
  return content
}

function compactVehicleLabel(vehicle: MapVehicle) {
  const plateDigits = vehicle.plate?.replace(/\D/g, '') ?? ''
  if (plateDigits.length >= 2) return plateDigits.slice(-2)
  return vehicle.label.length > 5 ? vehicle.label.slice(-5) : vehicle.label
}

function createVehicleTooltip(vehicle: MapVehicle) {
  const content = document.createElement('div')
  content.className = 'min-w-40'

  const title = document.createElement('strong')
  const detail = document.createElement('div')

  title.textContent = `${vehicle.routeCode} · ${vehicle.label}`
  const direction = vehicle.direction === 'outbound' ? 'Outbound' : 'Inbound'
  const freshness = vehicle.source === 'last-known' ? 'Last known · ' : ''
  const arrival = vehicle.minutesToNextStop !== undefined ? `${vehicle.minutesToNextStop} min` : ''
  detail.className = 'text-xs text-muted-foreground'
  detail.textContent =
    arrival && vehicle.nextStop
      ? `${freshness}${direction} · ${arrival} to ${vehicle.nextStop}`
      : vehicle.nextStop
        ? `${freshness}${direction} · Next: ${vehicle.nextStop}`
        : arrival
          ? `${freshness}${direction} · ${arrival} reported`
          : `${freshness}${direction}`

  // The details button is gone: the whole 48px marker now navigates, which is a
  // far better touch target than a 28px button inside a hover tooltip, and the
  // `viewVehicle` emit it existed to fire no longer has a second destination.
  content.append(title, detail)
  return content
}
</script>

<template>
  <div ref="map" class="relative z-0 size-full bg-muted" aria-label="Hanoi bus route map" />
</template>
